
import { downloadAirRoadJobsPDF, getAirRoad, lookupCustomer } from '@/api';
import LookupCustomerCode from '@/components/jobBooking/AdvancedJobBooking/LookupCustomerCode.vue';
import Header from '@/components/jobBooking/Header/Header.vue';
import DatePicker from '@/components/searchPage/DatePicker.vue';
import BackButton from '@/components/UI/backButton/BackButton.vue';
import Button from '@/components/UI/button/Button.vue';
import AirRoadJobsTable from '@/components/airRoad/AirRoadJobsTable.vue';
import StopDrawing from '@/components/jobDetailed/StopDrawing/StopDrawing.vue';
import PrintActionBtn from '@/components/btns/PrintActionBtn.vue';

import POD from '@/components/jobDetailed/POD/POD.vue';
import DateTimezoneToggle from '@/components/UI/dateTimezoneToggle/DateTimezoneToggle.vue';

import { User } from '@/models/main';
import { AirRoadJobData, AirRoadResponse, AirRoadJobsResponseData } from '@/models/airRoad';
import { mapState } from 'vuex';
import { format, utcToZonedTime } from 'date-fns-tz';
import { timeZoneDefault } from '@/utils/PortalConstants';
import { getARServiceData } from '@/utils/Common';
import { addDays } from 'date-fns';

import { Component, Vue, Watch } from 'vue-property-decorator';
import { notificationErrorText } from '@/utils/PortalConstants';

@Component({
  components: {
    AirRoadJobsTable,
    BackButton,
    Button,
    DatePicker,
    Header,
    LookupCustomerCode,
    POD,
    StopDrawing,
    DateTimezoneToggle,
    PrintActionBtn,
  },
  computed: {
    ...mapState('moduleUser', { user: 'user' }),
  },
})
export default class AirRoadBooked extends Vue {
  // LOADERS
  private loading = false;
  private loadingPdf = false;

  // USER
  private user!: User;

  // API
  private autocompleteCustomer = lookupCustomer;
  private getARServiceData = getARServiceData;

  // HEADER FILTERS:
  // DATE RANGE
  private dates: string[] = [];
  private datesDefault: string[] = [];

  // STATUSES
  private jobsStatuses = ['Booked', 'In transit', 'PoD received', 'Failed to book', 'Queued to book'];
  private jobsStatusesCodes = [1, 2, 3, 1001, 1002];
  private jobsStatusesCodesOrder = [0, 1002, 1, 2, 3];
  private jobStatusesOrder = ['Not booked', 'Queued to book', 'Booked', 'In transit', 'PoD received'];
  private chosenJobsStatus = '';
  private statusCode: number | null = null;

  // CUSTOMER CODE
  private customerCode = '';

  // SEARCH
  public searchValue = '';

  // PAGE COMPONENTS

  // TABLE DATA
  private tableData: AirRoadJobData[] = [];
  private tableOpenedJobIdx = -1;
  private chosenJobs: number[] = [];
  private allChosen = false;

  // PAGINATION
  private pageData: AirRoadJobsResponseData = {
    count: 0,
    next: '',
    previous: '',
    results: [],
  };
  private pagination = {
    page: 1,
    total: 0,
    perPage: '10',
    visible: 7,
    perPageOptions: ['1', '5', '10', '50', '100'],
  };

  // Date type values rh_job_date, ar_created, required
  private dateTypes = ['RH job date', 'AR created', 'Required'];
  private dateTypeChosen = 'rh_job_date';

  // ACTIONS
  //
  // DATE TYPE RADIO
  private dateTypeClick(event) {
    this.dateTypeChosen = event.target.value;
  }

  // DATE RANGE
  private changeDate(dates): void {
    this.dates = dates;
    this.pagination.page = 1;
  }
  private clearDateFilter() {
    this.dates = [];
    this.dates = [...this.datesDefault];
  }

  // STATUSES
  private changeJobsStatus(value): void {
    this.statusCode = this.jobsStatusesCodes[this.jobsStatuses.indexOf(value)];
    this.pagination.page = 1;
  }
  private clearJobsStatus(): void {
    this.statusCode = null;
    this.pagination.page = 1;
  }

  // CUSTOMER CODE
  private setCustomerCode(customerCode): void {
    if (customerCode) {
      this.customerCode = customerCode;
      this.pagination.page = 1;
    }
  }
  private clearCustomerCode(): void {
    this.customerCode = '';
  }

  // SEARCH
  private clearSearch(): void {
    this.searchValue = '';
    this.pagination.page = 1;
  }

  // Copy JSON icon click
  private copyText(text) {
    navigator.clipboard.writeText(JSON.stringify(text)).then(() => {
      this.$notify({
        group: 'network',
        text: 'JSON copied',
        type: 'success',
        duration: 3000,
      });
    });
  }

  /**
   * Downloads generated PDF from the server. Used in two cases
   * When we choose jobs by checking checkboxes. No idsToDownload is parsed. IDs taken from the this.chosenJobs
   * When we click on an icon in a job's row of a table.
   * @param ids optional list of AR job IDs. Not used for checkboxes case
   * @private
   */
  private downloadBulk(ids: number[] | undefined) {
    let idsToDownload: number[];
    if (ids !== undefined) {
      idsToDownload = ids;
    } else {
      idsToDownload = this.chosenJobs;
    }
    this.loadingPdf = true;
    downloadAirRoadJobsPDF(idsToDownload)
      .then(res => {
        const blob = new Blob([res.data], { type: 'pdf' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        //TODO: name the PDF file by connote(s), not by ar job id
        link.download = `${idsToDownload.join('-')}.pdf`;
        link.click();
        URL.revokeObjectURL(link.href);
        this.chosenJobs = [];
        this.allChosen = false;
      })
      .catch(() => {
        this.$notify({
          group: 'network',
          title: 'Error downloading labels',
          text: notificationErrorText,
          type: 'warn',
        });
      })
      .finally(() => {
        this.loadingPdf = false;
      });
  }

  // TABLE ROW CLICK
  private tableJobRowClick(idx) {
    if (this.tableOpenedJobIdx === idx) {
      this.tableOpenedJobIdx = -1;
    } else {
      this.tableOpenedJobIdx = idx;
    }
  }

  // TABLE CHECKBOXES
  private jobCheckboxClicked(id) {
    const jobChosenIdx = this.chosenJobs.findIndex(x => x === id);
    if (jobChosenIdx === -1) {
      this.chosenJobs.push(id);
    } else {
      this.chosenJobs.splice(jobChosenIdx, 1);
      if (this.allChosen) {
        this.allChosen = false;
      }
    }
  }
  // CHECKBOX ALL
  private jobCheckboxAllClicked() {
    const jobIds = this.tableData.reduce((acc: number[], obj) => {
      if (obj.labels) {
        acc.push(obj.id);
      }
      return acc;
    }, []);
    if (!this.allChosen) {
      this.chosenJobs = jobIds;
    } else {
      this.chosenJobs.length = 0;
    }
  }

  private async updatePagination() {
    await this.getAirRoadWrapper();
    // Pagination corrections:
    this.pagination.total = Math.ceil(this.pageData.count / Number(this.pagination.perPage));
    if (this.pagination.page === 0 && this.pagination.total >= 1) {
      this.pagination.page = 1;
    }
    if (this.pagination.page > this.pagination.total) {
      this.pagination.page = this.pagination.total;
    }
  }

  // WATCHERS
  //
  // PAGINATION
  @Watch('pagination.perPage')
  private perPageSelectChanged() {
    this.pagination.page = 1;
    this.chosenJobs.length = 0;
    this.allChosen = false;
    this.updatePagination();
  }

  // WRAPPERS
  //
  // TABLE DATA
  private getAirRoadWrapper(): void {
    // Sync search value with URL query
    const newQuery = { ...this.$route.query };
    if (this.searchValue) {
      newQuery.search = this.searchValue;
    } else {
      delete newQuery.search;
    }
    if (JSON.stringify(this.$route.query) !== JSON.stringify(newQuery)) {
      this.$router.push({ query: newQuery });
    }

    this.loading = true;
    const endpointParams = {
      ordering: '-rockhopper_date,eta',
      search: this.searchValue,
      rockhopper_date_gte: this.dateTypeChosen === 'rh_job_date' ? this.dates[0] : '',
      rockhopper_date_lte: this.dateTypeChosen === 'rh_job_date' ? this.dates[1] : '',
      created_at_gte: this.dateTypeChosen === 'ar_created' ? this.dates[0] : '',
      created_at_lte: this.dateTypeChosen === 'ar_created' ? this.dates[1] : '',
      required_at_gte: this.dateTypeChosen === 'required' ? this.dates[0] : '',
      required_at_lte: this.dateTypeChosen === 'required' ? this.dates[1] : '',
      status: this.statusCode,
      customer_code: this.customerCode,
      page: this.pagination.page,
      page_size: Number(this.pagination.perPage),
    };
    this.tableOpenedJobIdx = -1;
    getAirRoad(endpointParams)
      .then((res: AirRoadResponse) => {
        this.pageData = res;
        this.tableData = res.results;
        this.pagination.total = Math.ceil(res.count / Number(this.pagination.perPage));
      })
      .catch(() => {
        this.tableData.length = 0;
        this.pagination.page = 1;
        this.$notify({
          group: 'network',
          title: 'Error loading AirRoad data',
          text: notificationErrorText,
          type: 'warn',
        });
      })
      .finally(() => {
        this.loading = false;
      });
  }

  // COMPONENT LIFECYCLE EVENTS
  //
  private mounted() {
    const userTimeZone = this.user.timezone || timeZoneDefault;
    const fromDate = format(utcToZonedTime(addDays(new Date(), -7), userTimeZone), 'yyyy-MM-dd'); // today
    const toDate = format(utcToZonedTime(addDays(new Date(), 5), userTimeZone), 'yyyy-MM-dd'); // today
    this.datesDefault = [fromDate, toDate];
    this.dates = [...this.datesDefault];
    if (this.$route.query.search) {
      this.searchValue = this.$route.query.search.toString();
    }
    this.getAirRoadWrapper();
  }
}
